import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { setCredentials } from './authSlice'
import { useGoogleLoginMutation } from './authApiSlice'
import { GOOGLE_CLIENT_ID } from '../../config/google'

const GSI_SRC = 'https://accounts.google.com/gsi/client'

// The height of the form's own buttons (.form__submit-button, index.css).
// Google's button has no height option, so it is scaled to sit level with them.
const BUTTON_HEIGHT = 50
// Google will not render wider than this
const MAX_WIDTH = 400

// What Google's button measures, once it has been drawn and asked. It does
// not change for the life of the page, so it is learnt once and the redraws
// that follow — a resize, a change of language — take a single pass.
let natural = null

/**
 * Draws the button to fill `host`, standing as tall as the form's own buttons.
 *
 * Google is asked for a button narrower than the space by the factor it is
 * then scaled up by, so it lands back at the container's width. The scale is
 * uniform, so the button keeps Google's own proportions — only its size is
 * ours.
 */
const drawButton = (host, options) => {
    const room = host.clientWidth || 320

    const ask = (scale) => {
        window.google.accounts.id.renderButton(host, {
            ...options,
            width: Math.min(Math.round(room / scale), MAX_WIDTH)
        })
        const drawn = host.firstElementChild
        // Measured off the layout box, which the scale does not touch
        return { height: drawn?.offsetHeight || 0, width: drawn?.offsetWidth || 0 }
    }

    // First time through, a plain drawing to measure: its height, which has
    // no option, and the width it settles at.
    if (!natural?.height) natural = ask(1)

    const toHeight = BUTTON_HEIGHT / natural.height
    const got = ask(toHeight)

    // Tall enough to stand level with the form's buttons — but never wider
    // than the space it has. Asked for less than a width of its own that it
    // is willing to go below, Google returns that minimum instead, and on a
    // narrow phone scaling it up to full height would push it out past the
    // rest of the form. Better a few pixels short than overhanging.
    host.style.setProperty('--gsi-scale', Math.min(toHeight, room / got.width))
}

// Loads Google Identity Services once per page
let gsiPromise
const loadGsi = () => {
    if (window.google?.accounts?.id) return Promise.resolve()
    if (!gsiPromise) {
        gsiPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.src = GSI_SRC
            script.async = true
            script.onload = resolve
            script.onerror = () => {
                gsiPromise = undefined
                reject(new Error('Could not load Google sign-in'))
            }
            document.head.appendChild(script)
        })
    }
    return gsiPromise
}

/**
 * "Continue with Google" button. Signs in, or creates the account on first
 * use; the backend links it to an existing account with the same email.
 * Renders nothing when no client id is configured.
 */
const GoogleSignIn = ({ onError }) => {
    const { t, i18n } = useTranslation()
    const buttonRef = useRef(null)
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [googleLogin] = useGoogleLoginMutation()
    const [failed, setFailed] = useState(false)

    // The GSI callback is registered once; keep it pointing at fresh props
    const handleCredential = useRef()
    handleCredential.current = async ({ credential }) => {
        try {
            const { accessToken } = await googleLogin({ credential }).unwrap()
            // Choosing Google is choosing to stay signed in on this device
            localStorage.setItem('persist', 'true')
            dispatch(setCredentials({ accessToken }))
            navigate('/dash')
        } catch (err) {
            onError?.(err.status ? t('auth.errors.googleFailed') : t('auth.errors.noServerResponse'))
        }
    }

    // Google's own button, in Google's own livery. Only its size is ours.
    const draw = useCallback(() => {
        if (buttonRef.current) drawButton(buttonRef.current, {
            type: 'standard',
            theme: 'filled_black',
            size: 'large',
            shape: 'pill',
            text: 'continue_with',
            locale: i18n.language
        })
    }, [i18n.language])

    useEffect(() => {
        if (!GOOGLE_CLIENT_ID) return undefined
        let cancelled = false
        let observer

        loadGsi()
            .then(() => {
                if (cancelled || !buttonRef.current) return
                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: (response) => handleCredential.current(response),
                    ux_mode: 'popup'
                })
                draw()

                // The width Google is given is a number of pixels, fixed at the
                // moment it is drawn. Without this the button keeps the width
                // the window had when the page loaded — through a drag, a phone
                // turning, or a keyboard opening — and spills out of the form.
                let drawnAt = buttonRef.current.clientWidth
                observer = new ResizeObserver(([entry]) => {
                    const width = Math.round(entry.contentRect.width)
                    // A pixel either way is the browser rounding, not a resize
                    if (!width || Math.abs(width - drawnAt) <= 1) return
                    drawnAt = width
                    draw()
                })
                observer.observe(buttonRef.current)
            })
            .catch(() => !cancelled && setFailed(true))

        return () => {
            cancelled = true
            observer?.disconnect()
        }
    }, [draw])

    if (!GOOGLE_CLIENT_ID || failed) return null

    return (
        <div className="auth__google">
            <div className="auth__divider" role="separator"><span>{t('auth.or')}</span></div>
            <div ref={buttonRef} className="auth__google-button" />
        </div>
    )
}

export default GoogleSignIn
